import moment from "moment";
import yup from "src/validators/yup.Module";
import { validateCpfOuCnpj } from "src/validators/validateCnpj";
import { validatePlaca } from "src/validators/validatePlaca";
import { FormatarCNPJ, FormatarCPF } from "src/hook/Functions";
import { MDFeCIOTGerarInput } from "src/services/Fiscal.Service";
import {
  EFormaPagamentoMDFe,
  ETipoOperacaoCIOT,
  ETipoPagamentoCIOT,
  EUnidadeMedidaPesoMDFe,
  MDFeInput,
  ufOptions,
} from "../../MDFe.Dto";

export interface CiotVeiculoFormType {
  placa: string;
  numeroEixos?: string;
  rntrcVeiculo?: string;
}

export interface CiotContratanteFracionadaFormType {
  documento: string;
}

export interface CiotPagamentoFormType {
  tipoPagamento: string;
  formaPagamento: string;
  cpfCnpjCreditado: string;
  chavePix?: string;
  codigoInstituicaoFinanceira?: string;
  numeroAgencia?: string;
  numeroConta?: string;
  numeroParcela?: string;
  dataVencimento?: string;
  valorParcela?: number;
}

export interface CiotGerarFormType {
  tipoOperacao: string;
  documentoResponsavel: string;
  documentoDestinatario?: string;
  rntrc: string;
  rntrcContratante?: string;
  valorFrete?: number;
  dataInicioViagem: string;
  dataFimViagem: string;
  ufInicio: string;
  ufFim: string;
  codigoMunicipioInicio?: string;
  codigoMunicipioFim?: string;
  distanciaKm?: string;
  veiculos: CiotVeiculoFormType[];
  codigoNaturezaCarga?: string;
  pesoCarga?: number;
  tipoCarga?: string;
  contratantesCargaFracionada: CiotContratanteFracionadaFormType[];
  pagamentos: CiotPagamentoFormType[];
  indAltoDesempenho: boolean;
  indRetornoVazio: boolean;
  composicaoVeicular: boolean;
  contingencia: boolean;
  justificativaContingencia?: string;
  idOperacaoTransporte?: string;
  forcarNovo: boolean;
}

export interface CiotOpcaoMunicipio {
  value: string;
  label: string;
}

export interface CiotValoresIniciais {
  valores: CiotGerarFormType;
  opcoesMunicipioInicio: CiotOpcaoMunicipio[];
  opcoesMunicipioFim: CiotOpcaoMunicipio[];
  pendencias: string[];
}

const FORMATOS_DATA = [
  "YYYY-MM-DD",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY",
];

const apenasAlfanumericos = (v?: string | null) =>
  (v ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

const apenasDigitos = (v?: string | null) => (v ?? "").replace(/\D/g, "");

const tipoOperacaoNumerico = (v: unknown) => Number(v);

const exigeCarga = (tipo: unknown) => {
  const t = tipoOperacaoNumerico(tipo);
  return (
    t === ETipoOperacaoCIOT.CargaLotacao ||
    t === ETipoOperacaoCIOT.CargaFracionada
  );
};

export const formatarDocumentoCiot = (val?: string | null) => {
  const limpo = apenasAlfanumericos(val);
  if (!limpo) return "";
  if (!/[A-Z]/.test(limpo) && limpo.length <= 11) return FormatarCPF(limpo);
  return FormatarCNPJ(limpo);
};

const dataParaIso = (val?: string | null) => {
  if (!val) return undefined;
  const parsed = moment(val, FORMATOS_DATA, true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

const pagamentoTipoPorDados = (p: NonNullable<MDFeInput["pagamentos"]>[number]) => {
  if (p.chavePixBancario) return ETipoPagamentoCIOT.Pix;
  if (p.cnpjInstituicaoPagamento) return ETipoPagamentoCIOT.InstituicaoPagamento;
  if (p.codigoBanco && p.codigoAgencia) return ETipoPagamentoCIOT.ContaCorrente;
  return ETipoPagamentoCIOT.Pix;
};

/**
 * Copia os dados já digitados no MDF-e (rascunho) para compor o CIOT e lista
 * as pendências (campos obrigatórios do MDF-e que impedem a geração).
 */
export const montarValoresIniciaisCiot = (
  mdfe: MDFeInput | null | undefined,
  documentoResponsavel: string,
  rntrcConfiguracao?: string
): CiotValoresIniciais => {
  const pendencias: string[] = [];
  const m = (mdfe ?? {}) as Partial<MDFeInput>;

  const docResponsavel = apenasAlfanumericos(documentoResponsavel);
  if (!docResponsavel) {
    pendencias.push("CPF/CNPJ do responsável pelo CIOT");
  }

  const rntrc = apenasDigitos(m.rntrc || rntrcConfiguracao);
  if (!rntrc) pendencias.push("RNTRC (Identificação do MDF-e)");

  const ufInicio = (m.ufInicio || "").trim().toUpperCase();
  const ufFim = (m.ufFim || "").trim().toUpperCase();
  if (!ufInicio) pendencias.push("UF de início da viagem");
  if (!ufFim) pendencias.push("UF de fim da viagem");

  const placaTracao = apenasAlfanumericos(m.placaVeiculoTracao);
  if (!placaTracao) pendencias.push("Placa do veículo de tração");

  const carregamentos = m.municipiosCarregamento ?? [];
  const descargas = m.municipiosDescarga ?? [];
  if (carregamentos.length === 0) {
    pendencias.push("Município de carregamento");
  }
  if (descargas.length === 0) pendencias.push("Município de descarga");

  const opcoesMunicipioInicio = carregamentos.map((c) => ({
    value: String(c.codigoMunicipioCarregamento),
    label: `${c.nomeMunicipioCarregamento} (${c.codigoMunicipioCarregamento})`,
  }));
  const opcoesMunicipioFim = descargas.map((d) => ({
    value: String(d.codigoMunicipioDescarga),
    label: `${d.nomeMunicipioDescarga} (${d.codigoMunicipioDescarga})`,
  }));

  const pagamentosMdfe = m.pagamentos ?? [];
  const valorFrete = pagamentosMdfe.reduce(
    (acc, p) => acc + (Number(p.valorTotalContrato) || 0),
    0
  );
  if (pagamentosMdfe.length === 0 || valorFrete <= 0) {
    pendencias.push("Pagamento com valor total do contrato (etapa Pagamento)");
  }

  const pagamentos: CiotPagamentoFormType[] = [];
  pagamentosMdfe.forEach((p, i) => {
    const doc = apenasAlfanumericos(
      p.documento || p.cnpjBeneficiario || p.cpfBeneficiario
    );
    if (!doc) {
      pendencias.push(`CPF/CNPJ do beneficiário do pagamento #${i + 1}`);
    }
    const base: CiotPagamentoFormType = {
      tipoPagamento: String(pagamentoTipoPorDados(p)),
      formaPagamento: String(p.formaPagamento ?? EFormaPagamentoMDFe.AVista),
      cpfCnpjCreditado: formatarDocumentoCiot(doc),
      chavePix: p.chavePixBancario ?? "",
      codigoInstituicaoFinanceira: p.codigoBanco ?? "",
      numeroAgencia: p.codigoAgencia ?? "",
      numeroConta: "",
    };
    const parcelas = p.parcelas ?? [];
    if (Number(p.formaPagamento) === EFormaPagamentoMDFe.APrazo && parcelas.length) {
      parcelas.forEach((pc) => {
        pagamentos.push({
          ...base,
          numeroParcela: String(pc.numeroParcela ?? ""),
          dataVencimento: dataParaIso(pc.dataVencimento) ?? "",
          valorParcela: Number(pc.valorParcela) || undefined,
        });
      });
    } else {
      pagamentos.push(base);
    }
  });

  const pesoBruto = Number(m.pesoBrutoTotal) || 0;
  const pesoKg =
    Number(m.unidadeMedidaPeso) === EUnidadeMedidaPesoMDFe.TON
      ? pesoBruto * 1000
      : pesoBruto;
  if (pesoKg <= 0) pendencias.push("Peso bruto total da carga (etapa Totais)");

  const tipoCarga = m.produtoPredominante?.tipoCarga;
  if (!tipoCarga) pendencias.push("Tipo de carga do produto predominante");

  const placas = [placaTracao, ...(m.reboques ?? []).map((r) => apenasAlfanumericos(r.placa))]
    .filter(Boolean)
    .filter((p, i, arr) => arr.indexOf(p) === i);
  const rntrcProprietario = apenasDigitos(m.proprietarioVeiculoTracao?.rntrc);
  const veiculos: CiotVeiculoFormType[] = placas.map((placa, i) => ({
    placa,
    numeroEixos: "",
    rntrcVeiculo: i === 0 ? rntrcProprietario : "",
  }));

  const dataInicio =
    dataParaIso(m.previsaoInicioViagemDataHora) ??
    dataParaIso(m.emissaoDataHora) ??
    moment().format("YYYY-MM-DD");

  const ultimoFim = opcoesMunicipioFim[opcoesMunicipioFim.length - 1];

  return {
    valores: {
      tipoOperacao: String(ETipoOperacaoCIOT.CargaLotacao),
      documentoResponsavel: formatarDocumentoCiot(docResponsavel),
      documentoDestinatario: "",
      rntrc,
      rntrcContratante: "",
      valorFrete: valorFrete > 0 ? Math.round(valorFrete * 100) / 100 : undefined,
      dataInicioViagem: dataInicio,
      dataFimViagem: dataInicio,
      ufInicio,
      ufFim,
      codigoMunicipioInicio: opcoesMunicipioInicio[0]?.value ?? "",
      codigoMunicipioFim: ultimoFim?.value ?? "",
      distanciaKm: "",
      veiculos,
      codigoNaturezaCarga: apenasDigitos(m.produtoPredominante?.ncm).slice(0, 4),
      pesoCarga: pesoKg > 0 ? Math.round(pesoKg * 100) / 100 : undefined,
      tipoCarga: tipoCarga ? String(tipoCarga) : "",
      contratantesCargaFracionada: [],
      pagamentos,
      indAltoDesempenho: false,
      indRetornoVazio: false,
      composicaoVeicular: veiculos.length > 1,
      contingencia: false,
      justificativaContingencia: "",
      idOperacaoTransporte: "",
      forcarNovo: false,
    },
    opcoesMunicipioInicio,
    opcoesMunicipioFim,
    pendencias,
  };
};

const docValido = (v?: string | null) => {
  const limpo = apenasAlfanumericos(v);
  return limpo.length > 0 && validateCpfOuCnpj(limpo);
};

const ufsValidas = ufOptions.map((o) => o.value).filter(Boolean);

const naoVazio = (v: unknown) => String(v ?? "").trim().length > 0;

export const CiotGerarSchema = yup
  .object()
  .shape({
    tipoOperacao: yup
      .string()
      .required("Tipo de operação é obrigatório.")
      .oneOf(["1", "2", "3"], "Tipo de operação inválido."),
    documentoResponsavel: yup
      .string()
      .required("CPF/CNPJ do responsável é obrigatório.")
      .test("doc-resp", "CPF/CNPJ do responsável é inválido.", docValido),
    documentoDestinatario: yup
      .string()
      .nullable()
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) =>
          schema
            .required("CPF/CNPJ do destinatário é obrigatório para este tipo de operação.")
            .test("doc-dest", "CPF/CNPJ do destinatário é inválido.", docValido),
        otherwise: (schema) =>
          schema.test(
            "doc-dest-proibido",
            "Não informe o destinatário na operação TAC-Agregado.",
            (v) => !naoVazio(v)
          ),
      }),
    rntrc: yup
      .string()
      .required("RNTRC é obrigatório.")
      .test("rntrc", "RNTRC deve ter 8 ou 9 dígitos numéricos.", (v) =>
        /^\d{8,9}$/.test(apenasDigitos(v))
      ),
    rntrcContratante: yup
      .string()
      .nullable()
      .test("rntrc-contratante", "RNTRC do contratante deve ter 8 ou 9 dígitos.", (v) =>
        naoVazio(v) ? /^\d{8,9}$/.test(apenasDigitos(v)) : true
      ),
    valorFrete: yup
      .number()
      .typeError("Valor do frete é obrigatório.")
      .required("Valor do frete é obrigatório.")
      .moreThan(0, "Valor do frete deve ser maior que zero."),
    dataInicioViagem: yup
      .string()
      .required("Data de início é obrigatória.")
      .test("inicio-valido", "Data de início inválida.", (v) =>
        moment(v, "YYYY-MM-DD", true).isValid()
      )
      .when("contingencia", {
        is: (c: boolean) => !c,
        then: (schema) =>
          schema.test(
            "inicio-max",
            "Data de início deve ser no máximo 30 dias a partir de hoje.",
            (v) => !moment(v, "YYYY-MM-DD", true).isValid() ||
              !moment(v, "YYYY-MM-DD").isAfter(moment().add(30, "days"), "day")
          ),
      }),
    dataFimViagem: yup
      .string()
      .required("Data de fim é obrigatória.")
      .test("fim-valido", "Data de fim inválida.", (v) =>
        moment(v, "YYYY-MM-DD", true).isValid()
      )
      .test("fim-intervalo", "Data de fim deve ser igual/posterior ao início e o intervalo de no máximo 90 dias.", function (v) {
        const ini = moment(this.parent.dataInicioViagem, "YYYY-MM-DD", true);
        const fim = moment(v, "YYYY-MM-DD", true);
        if (!ini.isValid() || !fim.isValid()) return true;
        const dias = fim.diff(ini, "days");
        return dias >= 0 && dias <= 90;
      }),
    ufInicio: yup
      .string()
      .required("UF de início é obrigatória.")
      .oneOf(ufsValidas, "UF de início inválida."),
    ufFim: yup
      .string()
      .required("UF de fim é obrigatória.")
      .oneOf(ufsValidas, "UF de fim inválida."),
    codigoMunicipioInicio: yup
      .string()
      .nullable()
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) => schema.required("Município de início é obrigatório."),
      }),
    codigoMunicipioFim: yup
      .string()
      .nullable()
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) => schema.required("Município de fim é obrigatório."),
      }),
    distanciaKm: yup
      .string()
      .nullable()
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) =>
          schema
            .required("Distância (km) é obrigatória.")
            .test("distancia", "Distância deve ser maior que zero.", (v) => Number(apenasDigitos(v)) > 0),
      }),
    veiculos: yup
      .array()
      .of(
        yup.object().shape({
          placa: yup
            .string()
            .required("Placa é obrigatória.")
            .test("placa", "Placa inválida.", (v) => validatePlaca(v)),
          numeroEixos: yup
            .string()
            .required("Informe o nº de eixos.")
            .oneOf(["1", "2", "3", "4"], "Eixos deve ser de 1 a 4."),
          rntrcVeiculo: yup.string().nullable().notRequired(),
        })
      )
      .min(1, "Informe ao menos 1 veículo.")
      .max(5, "A ANTT aceita no máximo 5 veículos.")
      .test("placas-unicas", "Placas não podem se repetir.", (lista) => {
        const placas = (lista ?? []).map((v) => apenasAlfanumericos(v?.placa));
        return new Set(placas).size === placas.length;
      }),
    codigoNaturezaCarga: yup
      .string()
      .nullable()
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) =>
          schema
            .required("Natureza da carga é obrigatória.")
            .test("natureza", "Natureza da carga deve ser maior que zero.", (v) => Number(apenasDigitos(v)) > 0),
      }),
    pesoCarga: yup
      .number()
      .nullable()
      .transform((val, orig) => (orig === "" || orig === null ? undefined : val))
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) =>
          schema
            .required("Peso da carga é obrigatório.")
            .min(0.01, "Peso da carga mínimo é 0,01 kg.")
            .max(9999999.99, "Peso da carga máximo é 9.999.999,99 kg."),
      }),
    tipoCarga: yup
      .string()
      .nullable()
      .when("tipoOperacao", {
        is: exigeCarga,
        then: (schema) =>
          schema
            .required("Tipo de carga é obrigatório.")
            .oneOf(Array.from({ length: 12 }, (_, i) => String(i + 1)), "Tipo de carga inválido."),
      }),
    contratantesCargaFracionada: yup
      .array()
      .of(
        yup.object().shape({
          documento: yup
            .string()
            .required("CPF/CNPJ é obrigatório.")
            .test("doc-frac", "CPF/CNPJ inválido.", docValido),
        })
      )
      .when("tipoOperacao", {
        is: (t: unknown) => tipoOperacaoNumerico(t) === ETipoOperacaoCIOT.CargaFracionada,
        then: (schema) =>
          schema.min(1, "Informe ao menos 1 contratante da carga fracionada."),
      }),
    pagamentos: yup
      .array()
      .of(
        yup.object().shape({
          tipoPagamento: yup
            .string()
            .required("Tipo de pagamento é obrigatório.")
            .oneOf(["1", "2", "3", "4", "5", "6"], "Tipo de pagamento inválido."),
          formaPagamento: yup
            .string()
            .required("Forma de pagamento é obrigatória.")
            .oneOf(["0", "1"], "Forma de pagamento inválida."),
          cpfCnpjCreditado: yup
            .string()
            .required("CPF/CNPJ do recebedor é obrigatório.")
            .test("doc-creditado", "CPF/CNPJ do recebedor é inválido.", docValido),
          chavePix: yup
            .string()
            .nullable()
            .when("tipoPagamento", {
              is: (t: unknown) => Number(t) === ETipoPagamentoCIOT.Pix,
              then: (schema) => schema.required("Chave Pix é obrigatória."),
            }),
          codigoInstituicaoFinanceira: yup
            .string()
            .nullable()
            .when("tipoPagamento", {
              is: (t: unknown) => [2, 3, 4].includes(Number(t)),
              then: (schema) => schema.required("Banco é obrigatório."),
            }),
          numeroAgencia: yup
            .string()
            .nullable()
            .when("tipoPagamento", {
              is: (t: unknown) => [2, 3, 4].includes(Number(t)),
              then: (schema) => schema.required("Agência é obrigatória."),
            }),
          numeroConta: yup
            .string()
            .nullable()
            .when("tipoPagamento", {
              is: (t: unknown) => [2, 3, 4].includes(Number(t)),
              then: (schema) => schema.required("Conta é obrigatória."),
            }),
          numeroParcela: yup
            .string()
            .nullable()
            .when("formaPagamento", {
              is: (f: unknown) => Number(f) === EFormaPagamentoMDFe.APrazo,
              then: (schema) => schema.required("Nº da parcela é obrigatório."),
            }),
          dataVencimento: yup
            .string()
            .nullable()
            .when("formaPagamento", {
              is: (f: unknown) => Number(f) === EFormaPagamentoMDFe.APrazo,
              then: (schema) => schema.required("Vencimento é obrigatório."),
            }),
          valorParcela: yup
            .number()
            .nullable()
            .transform((val, orig) => (orig === "" || orig === null ? undefined : val))
            .when("formaPagamento", {
              is: (f: unknown) => Number(f) === EFormaPagamentoMDFe.APrazo,
              then: (schema) =>
                schema
                  .required("Valor da parcela é obrigatório.")
                  .moreThan(0, "Valor da parcela deve ser maior que zero."),
            }),
        })
      )
      .min(1, "Informe ao menos 1 pagamento.")
      .test(
        "pagamento-outros-tac",
        "Tipo de pagamento 'Outros' não é permitido na operação TAC-Agregado.",
        function (lista) {
          if (tipoOperacaoNumerico(this.parent.tipoOperacao) !== ETipoOperacaoCIOT.TacAgregado) {
            return true;
          }
          return !(lista ?? []).some(
            (p) => Number(p?.tipoPagamento) === ETipoPagamentoCIOT.Outros
          );
        }
      ),
    indAltoDesempenho: yup.boolean().default(false),
    indRetornoVazio: yup.boolean().default(false),
    composicaoVeicular: yup.boolean().default(false),
    contingencia: yup.boolean().default(false),
    justificativaContingencia: yup
      .string()
      .nullable()
      .when("contingencia", {
        is: true,
        then: (schema) =>
          schema
            .required("Justificativa da contingência é obrigatória.")
            .max(255, "Justificativa deve ter no máximo 255 caracteres."),
      }),
    idOperacaoTransporte: yup
      .string()
      .nullable()
      .when("contingencia", {
        is: true,
        then: (schema) =>
          schema
            .required("ID da operação de transporte é obrigatório na contingência.")
            .length(12, "ID da operação de transporte deve ter 12 caracteres."),
      }),
    forcarNovo: yup.boolean().default(false),
  });

const textoOuNull = (v?: string | null) => (naoVazio(v) ? String(v).trim() : null);

export const montarPayloadCiot = (f: CiotGerarFormType): MDFeCIOTGerarInput => {
  const comCarga = exigeCarga(f.tipoOperacao);
  const tipo = Number(f.tipoOperacao);
  const contingencia = Boolean(f.contingencia);

  return {
    tipoOperacao: tipo,
    documentoResponsavel: apenasAlfanumericos(f.documentoResponsavel),
    documentoDestinatario: comCarga ? apenasAlfanumericos(f.documentoDestinatario) : null,
    rntrc: apenasDigitos(f.rntrc),
    rntrcContratante: naoVazio(f.rntrcContratante) ? apenasDigitos(f.rntrcContratante) : null,
    valorFrete: Number(f.valorFrete) || 0,
    dataInicioViagem: f.dataInicioViagem,
    dataFimViagem: f.dataFimViagem,
    ufInicio: f.ufInicio,
    ufFim: f.ufFim,
    codigoMunicipioInicio: comCarga ? Number(f.codigoMunicipioInicio) : null,
    codigoMunicipioFim: comCarga ? Number(f.codigoMunicipioFim) : null,
    distanciaKm: comCarga ? Number(apenasDigitos(f.distanciaKm)) : null,
    veiculos: f.veiculos.map((v) => ({
      placa: apenasAlfanumericos(v.placa),
      numeroEixos: Number(v.numeroEixos),
      rntrcVeiculo: naoVazio(v.rntrcVeiculo) ? apenasDigitos(v.rntrcVeiculo) : null,
    })),
    carga: comCarga
      ? {
          codigoNaturezaCarga: Number(apenasDigitos(f.codigoNaturezaCarga)),
          pesoCarga: Number(f.pesoCarga),
          tipoCarga: Number(f.tipoCarga),
          contratantesCargaFracionada:
            tipo === ETipoOperacaoCIOT.CargaFracionada
              ? f.contratantesCargaFracionada.map((c) => apenasAlfanumericos(c.documento))
              : null,
        }
      : null,
    pagamentos: f.pagamentos.map((p) => {
      const tp = Number(p.tipoPagamento);
      const aPrazo = Number(p.formaPagamento) === EFormaPagamentoMDFe.APrazo;
      const conta = [
        ETipoPagamentoCIOT.ContaCorrente,
        ETipoPagamentoCIOT.ContaPoupanca,
        ETipoPagamentoCIOT.ContaPagamento,
      ].includes(tp);
      return {
        tipoPagamento: tp,
        formaPagamento: Number(p.formaPagamento),
        cpfCnpjCreditado: apenasAlfanumericos(p.cpfCnpjCreditado),
        chavePix: tp === ETipoPagamentoCIOT.Pix ? textoOuNull(p.chavePix) : null,
        identificadorPix: null,
        codigoPagamento: null,
        codigoInstituicaoFinanceira: conta ? Number(apenasDigitos(p.codigoInstituicaoFinanceira)) : null,
        numeroAgencia: conta ? textoOuNull(p.numeroAgencia) : null,
        numeroConta: conta ? textoOuNull(p.numeroConta) : null,
        numeroParcela: aPrazo ? Number(p.numeroParcela) : null,
        dataVencimento: aPrazo ? textoOuNull(p.dataVencimento) : null,
        valorParcela: aPrazo ? Number(p.valorParcela) : null,
      };
    }),
    indicadores:
      tipo === ETipoOperacaoCIOT.CargaLotacao
        ? {
            indAltoDesempenho: Boolean(f.indAltoDesempenho),
            indRetornoVazio: Boolean(f.indRetornoVazio),
            composicaoVeicular: Boolean(f.composicaoVeicular),
          }
        : null,
    contingencia,
    justificativaContingencia: contingencia ? textoOuNull(f.justificativaContingencia) : null,
    idOperacaoTransporte: contingencia ? textoOuNull(f.idOperacaoTransporte) : null,
    forcarNovo: Boolean(f.forcarNovo),
  };
};
